import { ArrowUpRightIcon } from "lucide-react";
import { toAbsoluteUrl } from "../../_theme/helpers";
import { useNavigate } from "react-router-dom";
import { noAccessReason, useAuth, useIsRightLocked } from "../modules/auth";
import { envConfig } from "../../utils/config";
import { Link } from "react-router-dom";
import VideoWithFallback from "./Components/VideoWithFallback";
import { withDisabledHint } from "./Components/withDisabledHint";
import { useIsDashboardTabLocked } from "../modules/Dashboard/core/tabRights";
// =========================
// Types
// =========================

interface HomeCard {
    id: number;
    title: string;
    description: string;
    imageUrl: string;
    ReadMoreUrl: string;
    // Dashboard tab that Enter opens.
    dashboardTab?: string;
    // Right needed to use the card's module when it is not a dashboard tab.
    right?: string;
}

interface ButtonProps {
    onClick?: () => void;
    disabledReason?: string;
}

// =========================
// Data
// =========================

const HOME_CARDS_DATA: HomeCard[] = [
    {
        id: 1,
        title: "Project Evaluation",
        description:
            "A dynamic decision-support tool guided by Panoptic Principles curating infrastructure projects into investment-ready projects that align with policy, resilience, and long-term public value.",
        imageUrl: "media/images/project-evoluation.png",
        ReadMoreUrl: "/pages/project-evaluation",
        dashboardTab: "evaluation",
    },
    {
        id: 2,
        title: "Investor Ready Portfolio",
        description:
            "A singular trusted source for investors to explore vetted, investment-ready Western Cape wide infrastructure projects.",
        imageUrl: "media/images/investment.png",
        ReadMoreUrl: "/pages/investor-prospectus",
        dashboardTab: "prospectus",
    },
    {
        id: 3,
        title: "Governance Tools & Methods",
        description:
            "Digitised compliance tool embedding transparency, reduce risk, and drive consistency across infrastructure planning.",
        imageUrl: "media/images/governance-resources.png",
        ReadMoreUrl: "/pages/governance-tools-and-methods",
        dashboardTab: "stagegates",
    },
    {
        id: 4,
        title: "Critical Gigamap",
        description:
            "Visualisation that intelligently reveals Monitoring and Evaluation Patterns, unlocking strategic insight.",
        imageUrl: "media/images/certificate.png",
        ReadMoreUrl: "/pages/critical-gigamaps",
    },
    {
        id: 5,
        title: "EVE",
        description:
            "Enhanced Virtual Experience (EVE) is the Infrastructure AI that allows the platform to provide instant expert support. Trained on infrastructure prescripts, methodologies and practices to reduce costs and streamline compliance in real time.",
        imageUrl: "media/images/DOIplatformInfrabotwhite.png",
        ReadMoreUrl: "/pages/artificial-intelligence",
        right: "eve",
    },
];

// =========================
// Components
// =========================

const EnterButton: React.FC<ButtonProps> = ({ onClick, disabledReason }) => withDisabledHint(
    <button
        onClick={onClick}
        disabled={Boolean(disabledReason)}
        className={`btn bg-white bg-opacity-20 rounded-3 text-white btn-sm fs-6 mt-2 enter-button d-flex align-items-center gap-1 ${disabledReason ? 'pe-none' : ''}`}
    >
        <span className="enter-text">Enter</span>
        <ArrowUpRightIcon size={18} className="enter-arrow" />
    </button>,
    disabledReason
);

const FlipCardFront: React.FC<{ imageUrl: string; title: string }> = ({
    imageUrl,
    title,
}) => (
    <div className="flip-card-front h-100 w-100 d-flex align-items-center justify-content-center flex-column">
        <img className="w-150px h-150px" src={`${toAbsoluteUrl(imageUrl)}`} alt={title} />
    </div>
);

const FlipCardBack: React.FC<{
    card: HomeCard;
    onEnter?: () => void;
}> = ({ card, onEnter }) => {
    const isTabLocked = useIsDashboardTabLocked();
    const isRightLocked = useIsRightLocked();
    const locked = (card.dashboardTab !== undefined && isTabLocked(card.dashboardTab))
        || (card.right !== undefined && isRightLocked(card.right));
    const disabledReason = locked ? noAccessReason(card.title) : undefined;

    return (
        <div className="flip-card-back h-100 w-100 d-flex align-items-center justify-content-center flex-column text-white">
            <div className="inner-flip-card-back h-100 w-100 d-flex align-items-center justify-content-center flex-column overflow-hidden text-center">
                <h6 className="fw-bold text-white">{card.title}</h6>

                <p className="text-light">{card.description}</p>

                <Link to={card.ReadMoreUrl} className="fw-bold text-white mb-0 mb-lg-3">Read More</Link>

                <EnterButton onClick={onEnter} disabledReason={disabledReason} />
            </div>
        </div>
    );
};

const FlipCard: React.FC<{
    card: HomeCard;
    onEnter?: () => void;
}> = ({ card, onEnter }) => (
    <div className="flip-card">
        <div className="flip-card-inner">
            <FlipCardFront imageUrl={card.imageUrl} title={card.title} />

            <FlipCardBack card={card} onEnter={onEnter} />
        </div>
    </div>
);

const BackgroundVideo: React.FC = () => {
    return (
        <>
            <VideoWithFallback
                videoSrc={`${envConfig.APP_MEDIA_URL}videos/public-pages/homeLogin.mp4`}
                fallbackSrc={`${envConfig.APP_MEDIA_URL}images/public-pages/homeLogin.webp`}
                className="position-fixed top-0 start-0 w-100 h-100"

            />
        </>
    );
};

const CardsGrid: React.FC<{
    cards: HomeCard[];
    onCardEnter?: (cardId: number) => void;
}> = ({ cards, onCardEnter }) => {
    const handleCardEnter = (cardId: number) => {
        onCardEnter?.(cardId);
    };

    return (
        <div className="row">
            <div className="col-12 pb-5 pb-xs-20 pb-sm-10 pb-md-0">
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-xl-5 g-10">
                    {cards.map((card) => (
                        <div className="col" key={card.id}>
                            <FlipCard
                                card={card}
                                onEnter={() => handleCardEnter(card.id)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

// =========================
// Main Component
// =========================

const Home: React.FC = () => {
    const navigate = useNavigate();
    const { auth } = useAuth();

    const handleCardEnter = (cardId: number) => {
        const dashboardTab = HOME_CARDS_DATA.find((card) => card.id === cardId)?.dashboardTab;
        if (dashboardTab) {
            navigate(`/dashboard/${dashboardTab}`);
            return;
        }

        switch (cardId) {
            case 4:
                {
                    auth ? window.open(
                        `${envConfig.APP_GIGAMAP_URL}`,
                        "_blank"
                    ) : navigate('/auth/login')
                }
                break;
            case 5:
                document
                    .querySelector(".chat-button")
                    ?.dispatchEvent(
                        new MouseEvent("click", {
                            bubbles: true,
                            cancelable: true,
                            view: window,
                        })
                    );
                break;
            default:
                break;
        }
    };

    return (
        <div className="wrapper">
            <BackgroundVideo />
            <section className="home-section hide-login d-flex align-items-lg-center align-items-sm-start align-items-xs-end h-100 pt-20 mt-10 mt-sm-20 mt-md-0 mb-0 mb-10 mb-md-0 position-relative">
                <div className="public-pages-container mt-xl-20 mb-xs-20">
                    <CardsGrid
                        cards={HOME_CARDS_DATA}
                        onCardEnter={handleCardEnter}
                    />
                </div>
            </section>

        </div>
    );
};

export default Home;

